import React, { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { createClient } from '@supabase/supabase-js';
import { Camera, Loader2, XCircle, CheckCircle2, AlertTriangle, RefreshCcw } from 'lucide-react';

const SUPABASE_URL = 'https://mdftijdjckugrdouslni.supabase.co';
const SUPABASE_KEY = 'sb_publishable_itbwTGCKUHx5Kg_FuYfT8g_iiZsVnUS';
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

export default function LectorQRCredencial({ onBack }) {
    const [scanning, setScanning] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [errorCritico, setErrorCritico] = useState(null);

    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const audioCtxRef = useRef(null);

    useEffect(() => {
        try {
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (e) {
            console.warn('Web Audio API not supported', e);
        }

        // Iniciar escaneo automáticamente al entrar
        startScanner();

        return () => stopScanner();
    }, []);

    // Effect para manejar el inicio real del lector cuando el elemento video ya está en el DOM
    useEffect(() => {
        let isCancelled = false;

        if (scanning && !controlsRef.current) {
            // Damos un pequeño respiro para que React renderice el elemento <video>
            const timer = setTimeout(async () => {
                if (!videoRef.current || isCancelled) return;

                try {
                    const codeReader = new BrowserMultiFormatReader();

                    // Intentamos listar dispositivos para buscar la cámara trasera
                    const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices();
                    const backCamera = videoDevices.find(device =>
                        /back|rear|trasera|entorno/i.test(device.label)
                    );

                    const deviceId = backCamera ? backCamera.deviceId : (videoDevices.length > 0 ? videoDevices[0].deviceId : undefined);

                    controlsRef.current = await codeReader.decodeFromVideoDevice(
                        deviceId,
                        videoRef.current,
                        (result, error) => {
                            if (result && !isCancelled) {
                                handleScan(result.getText());
                            }
                        }
                    );
                } catch (err) {
                    console.error('Error iniciando escáner:', err);
                    if (!isCancelled) {
                        setErrorCritico('No se pudo acceder a la cámara. Revisa los permisos y asegúrate de usar HTTPS o localhost.');
                        setScanning(false);
                    }
                }
            }, 300);

            return () => {
                isCancelled = true;
                clearTimeout(timer);
            };
        }
    }, [scanning]);

    const playBeep = (type) => {
        if (!audioCtxRef.current) return;
        const ctx = audioCtxRef.current;
        if (ctx.state === 'suspended') ctx.resume();

        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc.connect(gainNode);
        gainNode.connect(ctx.destination);

        if (type === 'success') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(800, ctx.currentTime);
            gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
            osc.start();
            osc.stop(ctx.currentTime + 0.15);
            if (navigator.vibrate) navigator.vibrate([100]);
        } else {
            osc.type = 'square';
            osc.frequency.setValueAtTime(300, ctx.currentTime);
            gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
            osc.start();
            osc.stop(ctx.currentTime + 0.3);
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
    };

    const stopScanner = () => {
        if (controlsRef.current) {
            try {
                controlsRef.current.stop();
            } catch (e) {
                console.warn('Error deteniendo el scanner:', e);
            }
            controlsRef.current = null;
        }
        setScanning(false);
    };

    const startScanner = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setErrorCritico('Tu navegador no soporta el acceso a la cámara o no estás en una conexión segura (HTTPS).');
            return;
        }
        setResultado(null);
        setErrorCritico(null);
        setScanning(true);
    };

    const handleScan = async (text) => {
        if (loading) return;

        try {
            // El QR usa saltos de línea (\n) según App.js, no pipes (|)
            if (!text.includes('DNI:')) {
                throw new Error('Formato QR irreconocible');
            }

            // Extraemos la línea del DNI (independientemente de si usa \n o |)
            const lines = text.split(/[\n|]/);
            const dniLine = lines.find(l => l.includes('DNI:'));

            // Limpiamos el DNI: eliminamos "DNI:", puntos y espacios para dejar solo números
            const dniRaw = dniLine.replace('DNI:', '').replace(/\D/g, '').trim();

            if (!dniRaw) throw new Error('DNI vacío u oculto en la lectura.');

            stopScanner();
            setLoading(true);

            const { data, error } = await supabase.rpc('fn_validar_ingreso_afiliado', {
                p_dni: dniRaw,
            });

            if (error) {
                console.error('Error RPC:', error);
                throw new Error('Error conectando a los servidores o DB Timeout.');
            }

            const respuesta = data;
            setResultado(respuesta);

            if (respuesta.autorizado) {
                playBeep('success');
            } else {
                playBeep('error');
            }
        } catch (err) {
            stopScanner();
            playBeep('error');
            setErrorCritico(err.message || 'Error de lectura de QR.');
        } finally {
            setLoading(false);
        }
    };

    const isOk = resultado?.autorizado;
    const isWarning = resultado && !resultado.autorizado && resultado.estado_codigo !== 'INEXISTENTE';

    return (
        <div className="flex flex-col min-h-screen bg-[#0f172a] font-sans text-slate-100">
            <header className="h-[80px] bg-[#1e293b] flex items-center justify-between px-6 border-b border-slate-700 shadow-lg shrink-0">
                <div className="flex items-center gap-4">
                    {onBack && (
                        <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors">
                            <span className="material-icons">arrow_back</span>
                        </button>
                    )}
                    <img
                        src="https://supaso.org/logo.png"
                        alt="SUPASO"
                        className="h-[40px] w-auto object-contain"
                    />
                </div>
                <button
                    onClick={scanning ? stopScanner : startScanner}
                    className="w-[50px] h-[50px] rounded-[50px] bg-[#012160] flex items-center justify-center text-white active:scale-95 transition-transform"
                >
                    {scanning ? <XCircle size={28} /> : <Camera size={28} />}
                </button>
            </header>

            {loading && (
                <div className="flex-1 flex flex-col items-center justify-center space-y-4">
                    <Loader2 size={64} className="text-[#3b82f6] animate-spin" />
                    <p className="text-sm text-slate-400 max-w-[200px] text-center font-bold">
                        Validando en base de datos. Aguarde.
                    </p>
                </div>
            )}

            {scanning && !loading && !resultado && !errorCritico && (
                <div className="flex-1 flex flex-col items-center justify-center p-6 bg-black relative">
                    <div className="relative overflow-hidden w-full max-w-sm aspect-square rounded-[20px] bg-[#1e293b] shadow-2xl border-4 border-slate-700">
                        <video
                            ref={videoRef}
                            className="w-full h-full object-cover"
                            muted
                            playsInline
                        />
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-[#ef4444] shadow-[0_0_15px_#ef4444] animate-[scan_2s_linear_infinite]" />
                    </div>
                    <p className="mt-8 text-center text-slate-400 px-4 text-sm font-semibold max-w-[280px]">
                        Apunte la cámara hacia el QR de la credencial digital del afiliado.
                    </p>
                </div>
            )}

            {errorCritico && !loading && (
                <div className="flex-1 flex flex-col items-center justify-center p-6 space-y-6 text-center">
                    <div className="w-[100px] h-[100px] bg-[#ef4444]/20 rounded-full flex items-center justify-center mb-2">
                        <AlertTriangle size={48} className="text-[#ef4444]" />
                    </div>
                    <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-red-400 to-red-600">
                        Error Crítico
                    </h2>
                    <p className="text-slate-400 bg-[#1e293b] p-4 rounded-[12px] shadow-sm max-w-xs text-sm">
                        {errorCritico}
                    </p>
                    <button
                        onClick={startScanner}
                        className="flex items-center gap-2 mt-4 px-8 py-3 bg-[#012160] hover:bg-blue-800 rounded-[50px] font-bold tracking-wide"
                    >
                        <RefreshCcw size={18} /> Reintentar Escaneo
                    </button>
                </div>
            )}

            {resultado && !loading && (
                <div className="flex-1 flex flex-col items-center p-6 space-y-6 relative">
                    {isOk ? (
                        <div className="w-24 h-24 bg-[#22c55e]/20 rounded-full flex items-center justify-center mt-6">
                            <CheckCircle2 size={56} className="text-[#22c55e]" />
                        </div>
                    ) : isWarning ? (
                        <div className="w-24 h-24 bg-[#ffc107]/20 rounded-full flex items-center justify-center mt-6">
                            <XCircle size={56} className="text-[#ffc107]" />
                        </div>
                    ) : (
                        <div className="w-24 h-24 bg-[#ef4444]/20 rounded-full flex items-center justify-center mt-6">
                            <XCircle size={56} className="text-[#ef4444]" />
                        </div>
                    )}

                    <h2
                        className={`text-2xl font-black tracking-tight text-center ${isOk ? 'text-[#22c55e]' : isWarning ? 'text-[#ffc107]' : 'text-[#ef4444]'
                            }`}
                    >
                        {isOk ? 'ACCESO AUTORIZADO' : 'ACCESO DENEGADO'}
                    </h2>

                    <div className="w-full max-w-sm space-y-4">
                        <div className="bg-[#1e293b] rounded-[12px] p-5 border border-slate-700/50 shadow-md">
                            <h3 className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">
                                Afiliado Analizado
                            </h3>
                            <p className="text-lg font-bold text-white mb-4">
                                {resultado.afiliado_nombre}
                            </p>

                            <h3 className="text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">
                                Respuesta del Sistema / Hub
                            </h3>
                            <p className="text-slate-300 text-sm">{resultado.mensaje}</p>

                            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-700">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Trace state:
                                </h3>
                                <code className="text-xs font-mono bg-slate-800 text-blue-400 px-2 py-1 rounded">
                                    {resultado.estado_codigo}
                                </code>
                            </div>
                        </div>

                        <button
                            onClick={startScanner}
                            className="w-full py-4 mt-8 bg-gradient-to-r from-[#012160] to-blue-800 rounded-[50px] font-bold text-white shadow-xl flex items-center justify-center gap-2 tracking-wide"
                        >
                            <Camera size={20} /> NUEVO ESCANEO
                        </button>
                    </div>
                </div>
            )}

            {/* ESTILOS GLOBALES ESTOS ANIMARAN LA LUZ ROJA DEL LECTOR */}
            <style dangerouslySetInnerHTML={{
                __html: `
        @keyframes scan {
          0% { top: 0; }
          50% { top: 100%; }
          100% { top: 0; }
        }
      `}} />
        </div>
    );
}
